import {
    AutocompleteInput,
    Create,
    DateInput,
    NumberInput,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    TextInput,
} from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Briefcase, Calendar, User } from "lucide-react";
import { required, useDataProvider } from "ra-core";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { AutoReferenceField } from "../documents/shared/AutoReferenceField";
import { FilteredContactInput } from "../misc/FilteredContactInput";

const RepeatOrderSelector = () => {
    const company_id = useWatch({ name: 'company_id' });
    const project_type = useWatch({ name: 'project_type' });
    const previous_project_id = useWatch({ name: 'previous_project_id' });
    const { setValue } = useFormContext();
    const dataProvider = useDataProvider();

    // Reset previous project if company changes or type changes
    useEffect(() => {
        if (project_type !== 'repeat_order' || !company_id) {
            setValue('previous_project_id', null);
        }
    }, [company_id, project_type, setValue]);

    // Handle auto-population
    useEffect(() => {
        if (previous_project_id) {
            dataProvider.getOne('projects', { id: previous_project_id }).then(({ data }) => {
                if (data.description) setValue('description', data.description, { shouldDirty: true });
                if (data.estimated_value) setValue('estimated_value', data.estimated_value, { shouldDirty: true });
                if (data.contact_id) setValue('contact_id', data.contact_id, { shouldDirty: true });
            });
        }
    }, [previous_project_id, dataProvider, setValue]);

    if (project_type !== 'repeat_order') return null;

    if (!company_id) {
        return (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Select a Client Company below to choose the previous project to copy.
            </div>
        );
    }

    return (
        <ReferenceInput source="previous_project_id" reference="projects" filter={{ company_id }}>
            <AutocompleteInput label="Select Previous Project to Copy From" className="w-full" optionText="name" />
        </ReferenceInput>
    );
};

export const ProjectCreate = () => {
    return (
        <Create title="Create New Project">
            <SimpleForm className="max-w-5xl mx-auto space-y-6">
                {/* Project Identity */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-slate-50/50">
                        <div className="flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-slate-500" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Project Information</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <AutoReferenceField docType="project" />
                            <TextInput source="name" label="Project Name" validate={required()} className="w-full" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <SelectInput
                                source="project_type"
                                label="Project Type"
                                defaultValue="new_order"
                                choices={[
                                    { id: 'new_order', name: 'New Order' },
                                    { id: 'repeat_order', name: 'Repeat Order' },
                                ]}
                                className="w-full"
                            />
                            <TextInput
                                source="description"
                                label="Project Description"
                                multiline
                                rows={2}
                                className="w-full"
                            />
                            <div className="col-span-full">
                                <RepeatOrderSelector />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Stakeholders */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-slate-50/50">
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5 text-slate-500" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Stakeholders</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <ReferenceInput source="company_id" reference="companies">
                                <AutocompleteInput label="Client Company" validate={required()} className="w-full" />
                            </ReferenceInput>
                            <FilteredContactInput label="Client Contact" className="w-full" />
                            <ReferenceInput source="project_manager" reference="sales">
                                <AutocompleteInput label="Project Manager" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                        </div>
                    </CardContent>
                </Card>

                {/* Timeline & Budget */}
                <Card className="w-full shadow-sm border-t-4 border-t-slate-900">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-slate-500" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Timeline & Budget</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <DateInput source="start_date" label="Start Date" validate={required()} />
                            <DateInput source="expected_completion" label="Expected Completion" />
                            <NumberInput
                                source="estimated_value"
                                label="Estimated Value (₹)"
                                className="w-full"
                            />
                        </div>
                    </CardContent>
                </Card>
            </SimpleForm>
        </Create>
    );
};
